/* eslint-disable @typescript-eslint/naming-convention */
import { clamp, isArray, isNumber } from 'lodash';
import { createCanvas } from '@/utils/file-kit';
import {
    MAX_ZOOM,
    MIN_THUMB_SIZE,
    MIN_ZOOM,
    RenderMode,
    type ILayerProps,
    type IPoint,
    type ISize,
} from '@/version-types';
import {
    invertTransform,
    makeBoundingBoxFromPoints,
    transformPoint,
    type IEdgelessPlugin,
    type IScrollInfo,
    type IService,
    type TBBox,
    type TBlindAreas,
    type TMat2D,
    type XY,
} from '../../shared';
import { EventGenerator } from '../events';
import type { ServiceManager } from '../service-manager';
import { isSameBlindAreas, mergeBlindAreas } from './blind-areas';
import { EdgelessBoundingBox } from './bounding-box';
import type { EdgelessFrame } from './edgeless-frame';
import { EdgelessObject } from './edgeless-object';
import { FocusPlugin } from './plugin/focus-plugin';
import { MediaPlugin } from './plugin/media-plugin';
import { RequestRender } from './request-render';

const __zoomEventName: string = 'edgeless:canvas:1';
const __panEventName: string = 'edgeless:canvas:2';
const __domRenderEventName: string = 'edgeless:canvas:3';
const __blindAreasChangeEventName: string = 'edgeless:canvas:4';

/** 每帧最多启动 4 个图层资源任务，避免异步任务的同步部分和微任务集中执行 */
const MAX_RESOURCE_LOADS_PER_FRAME = 4;
/** 资源初始化的每帧时间预算；单个任务不可中断，达到预算后推迟剩余任务 */
const RESOURCE_LOAD_FRAME_BUDGET_MS = 4;

export class EdgelessCanvasService extends RequestRender implements IService {
    /**
     * 视口变换
     * [zoom, 0, 0, zoom, translateX, translateY]
     */
    readonly viewportTransform: TMat2D;
    /** 高分辨率缩放比例 */
    readonly retinaScaling: number;
    /** 画布 */
    readonly canvas: HTMLCanvasElement;
    /** 画布上下文 */
    readonly ctx: CanvasRenderingContext2D;
    /** 空画布 */
    readonly placeholderCanvas: HTMLCanvasElement;
    /** 空画布上下文 */
    private __placeholderImageCtx: CanvasRenderingContext2D;
    /** 对象映射 */
    private __objectsMap: Record<string, EdgelessObject> = {};
    /** 待加载资源的对象，值为 true 表示首次插入，false 表示合并后的资源刷新 */
    private readonly __pendingObjectResources = new Map<EdgelessObject, boolean>();
    private __objectResourceFrame: number | null = null;
    /** 非无限画布尚未被激活、渲染或显式请求的资源，只登记，不启动 rAF */
    private readonly __deferredObjectResources = new Map<EdgelessObject, { frameId: number; isAdd: boolean }>();
    private readonly __deferredFrameResources = new Map<number, Set<EdgelessObject>>();
    /** 对象资源加载完成定时器 */
    private __loadedTimer: NodeJS.Timeout | null = null;
    /** 插件 */
    readonly plugins: {
        /** 聚焦插件 */
        focus: FocusPlugin;
        /** 媒体插件 */
        media: MediaPlugin;
    };
    // 脏frameId 列表
    readonly dirtyFrameIds: Set<number>;
    /** 插件的调用顺序列表 */
    private __pluginsSorted: IEdgelessPlugin[];
    /** 默认盲区, 剧中时有默认padding=40px */
    private __defaultBlindAreas: TBlindAreas = { top: 0, right: 0, bottom: 0, left: 0 };
    /** 动态盲区 */
    private __dynamicBlindAreas: Partial<TBlindAreas> | null = null;
    /** 居中缩放计数器 */
    private __fitViewportId: number = 0;
    /**
     * 内容区域居中缩放内边距
     * [上下, 左右]
     */
    private __fitPadding: number[] = [60, 40];
    /** 居中缩放动画时长 */
    private __fitDuration: number = 500;

    constructor(readonly service: ServiceManager) {
        super('图层渲染', '#0f0');

        const eventGenerator = new EventGenerator('edgeless:canvas', this, service);
        console.log('edgeless:canvas attrs ==>', eventGenerator.attrs);

        const { canvas, ctx } = createCanvas(1, 1);
        this.canvas = canvas;
        this.ctx = ctx;
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';

        // this.objects = [];
        this.retinaScaling = Math.min(Math.max(window.devicePixelRatio, 1), 2);
        this.viewportTransform = [1, 0, 0, 1, 0, 0];

        const { canvas: emptyCanvas, ctx: emptyImageCtx } = createCanvas(10, 10);
        this.placeholderCanvas = emptyCanvas;
        this.__placeholderImageCtx = emptyImageCtx;

        this.plugins = {
            focus: new FocusPlugin(this),
            media: new MediaPlugin(this, {
                onExtraRenderProcess: () => {
                    // 聚焦渲染需要在最后
                    this.plugins.focus.render();
                },
            }),
        };
        this.__pluginsSorted = [this.plugins.media, this.plugins.focus];

        this.dirtyFrameIds = new Set();
    }

    onInit() {
        if (this.__objectResourceFrame !== null) {
            cancelAnimationFrame(this.__objectResourceFrame);
        }
        this.__objectResourceFrame = null;
        this.__pendingObjectResources.clear();
        this.__deferredObjectResources.clear();
        this.__deferredFrameResources.clear();
        // this.__destroyed = true;
        this.__objectsMap = {};
        if (this.__loadedTimer) {
            clearTimeout(this.__loadedTimer);
        }
        this.__loadedTimer = null;
        this.dirtyFrameIds.clear();
        this.setScrollInfo(null);
        this.setIsAutoScale(true);
    }

    /** 获取当前缩放比例 */
    getCurrentScrollZoom() {
        if (this.isAutoScale) {
            const padding = this.__fitPadding[1] * 2;
            if (this.isSingleMode) {
                // 单画布模式下，获取当前激活舞台的尺寸
                const frameSize = this.service.frame.getSize();
                if (!frameSize.width || !frameSize.height) return this.zoom;

                const horizontalZoom = (this.size.width - padding) / frameSize.width;
                const verticalZoom = (this.size.height - this.__fitPadding[0] * 2) / frameSize.height;
                return Math.min(horizontalZoom, verticalZoom, 1);
            } else if (this.isLongImageMode) {
                // 长图模式下，获取当前激活舞台的尺寸
                const horizontalZoom = (this.size.width * (3.5 / 5) - padding) / this.service.frame.getSize().width;
                return Math.min(horizontalZoom, 1);
            }
        }
        return this.zoom;
    }

    /** 计算并设置画布变换位置和缩放比例，实现自动剧中 */
    calcViewportTransform() {
        if (this.isEdgelessMode) return;

        const { horizontal, vertical, box, scale } = this.__calcScrollInfo();
        let tx = this.translateX;
        if (horizontal.needScrollbar) {
            if (!horizontal.offset.left && horizontal.offset.right) {
                tx = this.__fitPadding[1];
            } else if (horizontal.offset.left && !horizontal.offset.right) {
                tx = -(box.width * scale - this.size.width) - this.__fitPadding[1];
            } else if (!horizontal.offset.left && !horizontal.offset.right) {
                tx = (this.size.width - box.width * scale) / 2;
            }
        } else {
            tx = (this.size.width - box.width * scale) / 2;
        }

        let ty = this.translateY;
        if (vertical.needScrollbar) {
            if (!vertical.offset.top && vertical.offset.bottom) {
                ty = this.__fitPadding[0];
            } else if (vertical.offset.top && !vertical.offset.bottom) {
                ty = -(box.height * scale - this.size.height) - this.__fitPadding[0];
            } else if (!vertical.offset.top && !vertical.offset.bottom) {
                ty = (this.size.height - box.height * scale) / 2;
            } else if (ty == 0) {
                ty = vertical.offset.top;
            }
        } else {
            ty = (this.size.height - box.height * scale) / 2;
        }
        // console.log('zoomToPoint 22==>', cloneDeep({ horizontal, vertical, tx, ty, box, ory: this.translateY }));
        this.setViewportTransform([scale, 0, 0, scale, tx, ty]);
        // this.renderAll();
        this.requestRenderAll();
    }

    refreshScrollInfo() {
        this.setScrollInfo(this.__calcScrollInfo());
    }

    onBindEvent() {
        // 监听画布尺寸变化，重新设置画布尺寸
        this.onSizeChange((size) => {
            this.__setDimensions(size);
            if (this.isEdgelessMode) {
                this.refreshScrollInfo();
            } else {
                this.calcViewportTransform();
            }
        });

        // 监听是否自动缩放变化，如果自动缩放，则将激活舞台居中
        this.onIsAutoScaleChange(() => this.calcViewportTransform());

        // 画布大小变化，重新计算居中
        this.service.frame.onResize((frameId) => {
            if (frameId === this.service.frame.getActiveFrameId()) {
                this.calcViewportTransform();
            }
        });

        this.service.frame.onActiveIndexChange(() => {
            this.requestFrameResources(this.service.frame.getActiveFrameId());
        });

        let requestAnimationFrameId = 0;
        // 监听图层数据变化，做更新或者创建对象
        this.service.flatLayer.onChange((layer, onlyUpdateBaseInfo) => {
            cancelAnimationFrame(requestAnimationFrameId);
            requestAnimationFrameId = requestAnimationFrame(() => {
                this.refreshScrollInfo();
            });
            if (!layer) return;
            this.__updateLayerObject(layer, onlyUpdateBaseInfo);
        });

        this.service.frame.onRuntimeLayerChange((layer, onlyUpdateBaseInfo) => {
            cancelAnimationFrame(requestAnimationFrameId);
            requestAnimationFrameId = requestAnimationFrame(() => {
                this.refreshScrollInfo();
            });
            if (!layer) return;
            this.__updateLayerObject(layer, onlyUpdateBaseInfo);
        });

        // 监听图层删除，做删除对象
        this.service.flatLayer.onDelete((layerIds, dirtyFrameId) => {
            this.addDirtyFrameId(dirtyFrameId);
            for (const layerId of layerIds) {
                const obj = this.getByLayerId(layerId);
                if (obj) {
                    this.remove(obj);
                    // 如果对象有 domRender，则触发 domRender 事件
                    if (obj.domRender) {
                        this.__emitDomRender(obj, false, true);
                    }
                }
            }
            this.requestRenderAll();
        });

        this.service.frame.onRuntimeLayerDelete(({ layerIds, frameId }) => {
            this.addDirtyFrameId(frameId);
            for (const layerId of layerIds) {
                const obj = this.getByLayerId(layerId);
                if (obj) {
                    this.remove(obj);
                    if (obj.domRender) {
                        this.__emitDomRender(obj, false, true);
                    }
                }
            }
            this.requestRenderAll();
        });

        // 监听舞台重新渲染, 排序操作会触发
        this.service.frame.onSortLayer((frameId) => {
            this.addDirtyFrameId(frameId);
            this.requestRenderAll();
            console.log('onSortLayer=>', frameId);
        });

        // 监听主题变化，重新生成空画布
        this.service.editor.onThemeChange(this.__fillPlaceholderCanvas.bind(this));

        // 监听渲染模式变化，重新生成空画布
        this.onRenderModeChange(() => {
            this.__fillPlaceholderCanvas();
            if (this.isEdgelessMode) {
                for (const frameId of this.__deferredFrameResources.keys()) this.requestFrameResources(frameId);
            }
        });

        // 填充占位图
        this.__fillPlaceholderCanvas();

        // 监听平移事件，计算滚动信息
        this.onPan(() => {
            this.refreshScrollInfo();
            this.requestRenderAll();
        });

        // 隐藏图层
        this.onHiddenAllChange(() => {
            this.requestRenderAll();
        });
    }

    /** 按图层类型复用或重建渲染对象，兼容 AI 花字转换及撤销重做 */
    private __updateLayerObject(layer: ILayerProps, onlyUpdateBaseInfo = false) {
        const object = this.getByLayerId(layer.id);
        if (object?.layerType === layer.layerType) {
            const deferred = this.__deferredObjectResources.get(object);
            if (deferred && deferred.frameId !== layer.__originIndex) {
                this.__deferObjectResource(object, deferred.isAdd);
            }
            if (!this.isEdgelessMode && !onlyUpdateBaseInfo) {
                this.__deferObjectResource(object, this.__pendingObjectResources.get(object) ?? false);
                void object.update(true);
                this.requestRenderAll();
                return;
            }
            // 插入后的缩放、布局和协作更新继续同步几何，首次资源只消费出队时的最新数据。
            void object.update(
                this.__pendingObjectResources.has(object) ||
                    this.__deferredObjectResources.has(object) ||
                    onlyUpdateBaseInfo,
            );
            if (this.__pendingObjectResources.get(object)) {
                this.__updatePendingObjectLoading(object);
            }
            return;
        }
        if (object) {
            this.remove(object);
            if (object.domRender) {
                this.__emitDomRender(object, false, true);
            }
        }
        const nextObject = EdgelessObject.create(layer, this.service);
        if (nextObject) {
            this.__initLayerObject(nextObject);
        }
    }

    /** 同步注册图层，并按图层声明决定资源直接加载或分帧初始化 */
    private __initLayerObject(object: EdgelessObject) {
        this.add(object);
        if (!this.isEdgelessMode) {
            void object.update(true, true);
            this.__deferObjectResource(object, true);
            // 协同可能把图层插入长图中可见但非激活的页面；重绘时仅加载可见页资源。
            this.requestRenderAll();
            return;
        }
        if (!object.useRAF) {
            void object.update(false, true);
            return;
        }
        void object.update(true, true);
        this.__pendingObjectResources.set(object, true);
        this.__updatePendingObjectLoading(object);
        this.__scheduleObjectResources();
    }

    /** 登记页内资源变化，已进入 rAF 的任务只合并更新，未触发的页面继续等待 */
    private __deferObjectResource(object: EdgelessObject, isAdd: boolean) {
        if (this.__pendingObjectResources.has(object)) return;
        const layer = this.service.flatLayer.getLayer(object.layerId);
        const frameId = layer?.__originIndex;
        if (!isNumber(frameId)) return;
        const previous = this.__deferredObjectResources.get(object);
        if (previous) {
            const objects = this.__deferredFrameResources.get(previous.frameId);
            objects?.delete(object);
            if (!objects?.size) this.__deferredFrameResources.delete(previous.frameId);
        }
        const objects = this.__deferredFrameResources.get(frameId) ?? new Set<EdgelessObject>();
        objects.add(object);
        this.__deferredFrameResources.set(frameId, objects);
        this.__deferredObjectResources.set(object, { frameId, isAdd: previous?.isAdd || isAdd });
        // 当前页新增图层时未必会再次触发画布绘制，主动启动资源加载以免一直显示占位图。
        if (frameId === this.service.frame.getActiveFrameId()) {
            this.requestFrameResources(frameId);
        }
    }

    /**
     * 判断页面是否仍有等待显式触发的资源，供预览复用旧缓存而不提前绘制资源
     *
     * @param frameId - 稳定的页面 ID
     * @returns 页面存在尚未触发的资源时返回 true
     */
    hasDeferredFrameResources(frameId: number): boolean {
        return this.__deferredFrameResources.has(frameId);
    }

    /**
     * 显式请求指定页的待加载资源，供激活页、画布绘制及可见导航预览调用
     *
     * 基础信息已同步就绪；此处仅将该页资源（包括文字子集收集）交给有预算的 rAF。
     * 已请求或已完成的资源不会重复入队，也不会顺带加载其他页面。
     *
     * 副作用：启动该页待加载资源的 rAF，并登记首次加载状态
     *
     * @param frameId - 稳定的页面 ID，对应图层 __originIndex，而非导航排序索引
     */
    requestFrameResources(frameId: number) {
        const objects = this.__deferredFrameResources.get(frameId);
        if (!objects) return;
        this.__deferredFrameResources.delete(frameId);
        for (const object of objects) {
            const task = this.__deferredObjectResources.get(object);
            this.__deferredObjectResources.delete(object);
            if (!task || this.getByLayerId(object.layerId) !== object) continue;
            this.__pendingObjectResources.set(object, task.isAdd);
            if (task.isAdd) this.__updatePendingObjectLoading(object);
        }
        this.__scheduleObjectResources();
    }

    /** 将首次插入的可见资源纳入加载状态，DOM 和无像素资源的图层无需等待资源提示 */
    private __updatePendingObjectLoading(object: EdgelessObject) {
        if (object.domRender || object.noRender) return;
        // 子图层可能先于父组注册；出队时按最新祖先链判断，不能使用尚未 update 的可见性缓存。
        if (!this.service.flatLayer.checkIsNeedHidden(object.layerId)) {
            this.service.loading.enter(object.layerId);
        } else {
            this.service.loading.exit(object.layerId);
        }
    }

    /**
     * 合并父级拼图等关联图层的资源刷新请求，共享插入图层的每帧预算
     *
     * 副作用：在 rAF 中按最新图层数据刷新资源，保留已有任务的首次插入状态。
     *
     * @param object - 仍在当前画布中注册的渲染对象
     */
    requestObjectResourceUpdate(object: EdgelessObject) {
        if (this.getByLayerId(object.layerId) !== object) return;
        const deferred = this.__deferredObjectResources.get(object);
        if (deferred) {
            this.requestFrameResources(deferred.frameId);
        } else if (!this.__pendingObjectResources.has(object)) {
            this.__pendingObjectResources.set(object, false);
        }
        this.__scheduleObjectResources();
    }

    /** 将批量插入和关联资源刷新拆到多个动画帧，避免一个 rAF 回调仍然阻塞整批图层 */
    private __scheduleObjectResources() {
        if (this.__objectResourceFrame !== null || !this.__pendingObjectResources.size) {
            return;
        }
        this.__objectResourceFrame = requestAnimationFrame(() => {
            const start = performance.now();
            let count = 0;
            try {
                for (const [object, isAdd] of this.__pendingObjectResources) {
                    this.__pendingObjectResources.delete(object);
                    if (this.getByLayerId(object.layerId) !== object) continue;

                    if (isAdd) this.__updatePendingObjectLoading(object);
                    void object.update(false, isAdd);
                    count += 1;
                    if (
                        count >= MAX_RESOURCE_LOADS_PER_FRAME ||
                        performance.now() - start >= RESOURCE_LOAD_FRAME_BUDGET_MS
                    ) {
                        break;
                    }
                }
            } finally {
                this.__objectResourceFrame = null;
                this.__scheduleObjectResources();
            }
        });
    }

    /** 取消被删除或替换的对象尚未启动的资源加载 */
    private __cancelObjectResource(object: EdgelessObject) {
        const deferred = this.__deferredObjectResources.get(object);
        if (deferred) {
            this.__deferredObjectResources.delete(object);
            const objects = this.__deferredFrameResources.get(deferred.frameId);
            objects?.delete(object);
            if (!objects?.size) this.__deferredFrameResources.delete(deferred.frameId);
        }
        const isAdd = this.__pendingObjectResources.get(object);
        if (!this.__pendingObjectResources.delete(object)) return;
        if (isAdd && !object.domRender && !object.noRender) {
            this.service.loading.exit(object.layerId);
        }
        if (!this.__pendingObjectResources.size && this.__objectResourceFrame !== null) {
            cancelAnimationFrame(this.__objectResourceFrame);
            this.__objectResourceFrame = null;
        }
    }

    /**
     * 计算指定盒子的滚动信息
     * @param box - 盒子
     * @param scale - 缩放比例
     * @param translateX - 平移 X
     * @param translateY - 平移 Y
     * @returns 滚动信息
     */
    private __getBoxScrollInfo(box: TBBox, scale: number, translateX: number, translateY: number) {
        const { width: viewportWidth, height: viewportHeight } = this.size;

        const pdV = Math.floor(this.__fitPadding[0] / scale);
        const pdH = Math.floor(this.__fitPadding[1] / scale);

        // 单画布模式下，需要计算舞台边缘的空白区域
        const bx = { ...box };
        if (!this.isEdgelessMode) {
            bx.left -= pdH;
            bx.top -= pdV;
            bx.width += pdH * 2;
            bx.height += pdV * 2;
        }

        // 内容区域偏移量
        const contentLeft = bx.left * scale + translateX;
        const contentTop = bx.top * scale + translateY;
        const contentWidth = bx.width * scale;
        const contentHeight = bx.height * scale;
        const contentRight = contentLeft + contentWidth;
        const contentBottom = contentTop + contentHeight;

        // 滚动条偏移量
        const offsetLeft = contentLeft < 0 ? Math.abs(contentLeft) : 0;
        const offsetRight = contentRight > viewportWidth ? contentRight - viewportWidth : 0;
        const offsetTop = contentTop < 0 ? Math.abs(contentTop) : 0;
        const offsetBottom = contentBottom > viewportHeight ? contentBottom - viewportHeight : 0;

        // 是否需要滚动条
        let needHorizontalScrollbar = offsetLeft > 0 || offsetRight > 0;
        let needVerticalScrollbar = offsetTop > 0 || offsetBottom > 0;
        if (!this.isEdgelessMode) {
            needHorizontalScrollbar = bx.width * scale > viewportWidth;
            needVerticalScrollbar = bx.height * scale > viewportHeight;
        }

        // 滚动条宽高
        const horizontalThumbWidth = Math.max(MIN_THUMB_SIZE, viewportWidth - offsetLeft - offsetRight);
        const verticalThumbHeight = Math.max(MIN_THUMB_SIZE, viewportHeight - offsetTop - offsetBottom);

        // 滚动条总宽高
        const totalWidth = horizontalThumbWidth + offsetLeft + offsetRight;
        const totalHeight = verticalThumbHeight + offsetTop + offsetBottom;

        /** 滚动步长 */
        let horizontalStep = totalWidth / viewportWidth;
        let verticalStep = totalHeight / viewportHeight;

        if (!this.isEdgelessMode) {
            if (!needHorizontalScrollbar) {
                horizontalStep = 0;
            }
            if (!needVerticalScrollbar) {
                verticalStep = 0;
            }
        }

        return {
            box,
            scale,
            minThumbSize: MIN_THUMB_SIZE,
            horizontal: {
                needScrollbar: needHorizontalScrollbar,
                offset: {
                    left: Math.ceil(offsetLeft),
                    right: Math.ceil(offsetRight),
                },
                thumbWidth: Math.ceil(horizontalThumbWidth),
                totalWidth: Math.ceil(totalWidth),
                step: horizontalStep,
            },
            vertical: {
                needScrollbar: needVerticalScrollbar,
                offset: {
                    top: Math.ceil(offsetTop),
                    bottom: Math.ceil(offsetBottom),
                },
                thumbHeight: Math.ceil(verticalThumbHeight),
                totalHeight: Math.ceil(totalHeight),
                step: verticalStep,
            },
        };
    }

    /**
     * 修复单画布模式下的平移偏移量，防止滚动条被拖动到边界时，无法继续拖动
     * @param scrollInfo - 滚动信息
     * @param dx - 水平偏移量
     * @param dy - 垂直偏移量
     * @returns 修复后的偏移量
     */
    private __fixDeltaXY(scrollInfo: IScrollInfo, dx: number, dy: number) {
        const { horizontal, vertical } = scrollInfo;
        let x = 0;
        let y = 0;
        if (horizontal.needScrollbar) {
            if (dx > 0) {
                const offset = horizontal.offset.left;
                x = offset - dx < 0 ? offset : dx;
            } else if (dx < 0) {
                const offset = horizontal.offset.right;
                x = offset + dx < 0 ? -offset : dx;
            }
        }
        if (vertical.needScrollbar) {
            if (dy > 0) {
                const offset = vertical.offset.top;
                y = offset - dy < 0 ? offset : dy;
            } else if (dy < 0) {
                const offset = vertical.offset.bottom;
                y = offset + dy < 0 ? -offset : dy;
            }
        }

        return { x, y };
    }

    /** 计算滚动信息 */
    private __calcScrollInfo(zoom?: number) {
        const box = this.calcContentBox();
        const info = this.__getBoxScrollInfo(
            box,
            zoom ?? this.getCurrentScrollZoom(),
            this.translateX,
            this.translateY,
        );
        return info;
    }

    /** 填充占位图 */
    private __fillPlaceholderCanvas() {
        if (!this.isEdgelessMode) {
            this.__placeholderImageCtx.clearRect(0, 0, this.placeholderCanvas.width, this.placeholderCanvas.height);
        } else {
            this.__placeholderImageCtx.fillStyle = getComputedStyle(document.documentElement)
                .getPropertyValue('--background-system-frame-floatpanel')
                .trim();
            console.log('fillPlaceholderCanvas ==>', this.__placeholderImageCtx.fillStyle);
            this.__placeholderImageCtx.fillRect(0, 0, this.placeholderCanvas.width, this.placeholderCanvas.height);
        }
        this.requestRenderAll();
    }

    /** 监听平移事件 */
    onPan(fn: (translateX: number, translateY: number) => void, widget?: number) {
        return this.service.events.on(__panEventName, fn, widget);
    }

    /** 监听缩放事件 */
    onZoom(fn: (zoom: number) => void, widget?: number) {
        return this.service.events.on(__zoomEventName, fn, widget);
    }

    /** 监听盲区变化事件 */
    onBlindAreasChange(fn: (blindAreas: TBlindAreas) => void, widget?: number) {
        return this.service.events.on(__blindAreasChangeEventName, fn, widget);
    }

    /** 根据图层 ID 获取对象 */
    getByLayerId(layerId: string) {
        return this.__objectsMap[layerId] || null;
    }

    /** 滚动信息 */
    readonly scrollInfo: IScrollInfo | null = null;
    declare getScrollInfo: () => IScrollInfo | null;
    declare setScrollInfo: (scrollInfo: IScrollInfo | null) => void;
    declare onScrollInfoChange: (fn: (scrollInfo: IScrollInfo | null) => void) => number;

    /** 渲染模式 */
    readonly renderMode: RenderMode = RenderMode.SINGLE;
    declare getRenderMode: () => RenderMode;
    declare setRenderMode: (renderMode: RenderMode) => void;
    declare onRenderModeChange: (fn: (renderMode: RenderMode) => void, widget?: number) => number;

    /** viewport 尺寸 */
    readonly size: ISize = { width: 0, height: 0 };
    declare getSize: () => ISize;
    declare setSize: (size: ISize) => void;
    declare onSizeChange: (fn: (size: ISize) => void, widget?: number) => number;

    /** 是否自动缩放 */
    readonly isAutoScale: boolean = true;
    declare getIsAutoScale: () => boolean;
    declare setIsAutoScale: (isAutoScale: boolean) => void;
    declare onIsAutoScaleChange: (fn: (isAutoScale: boolean) => void) => number;

    /** 最小缩放 */
    readonly minZoom: number = MIN_ZOOM;
    declare getMinZoom: () => number;
    declare setMinZoom: (minZoom: number) => void;
    declare onMinZoomChange: (fn: (minZoom: number) => void) => number;

    /** 最大缩放 */
    readonly maxZoom: number = MAX_ZOOM;
    declare getMaxZoom: () => number;
    declare setMaxZoom: (maxZoom: number) => void;
    declare onMaxZoomChange: (fn: (maxZoom: number) => void) => number;

    /** 是否隐藏所有元素 */
    readonly hiddenAll: boolean = false;
    declare getHiddenAll: () => boolean;
    declare setHiddenAll: (hiddenAll: boolean) => void;
    declare onHiddenAllChange: (fn: (hiddenAll: boolean) => void) => number;

    /**
     * 设置画布尺寸
     * @param width - 画布宽度
     * @param height - 画布高度
     */
    private __setDimensions({ width, height }: ISize) {
        this.canvas.width = width * this.retinaScaling;
        this.canvas.height = height * this.retinaScaling;
        this.canvas.style.width = `${width}px`;
        this.canvas.style.height = `${height}px`;
        this.renderAll();
        // console.log('setDimensions ==>', this.canvas.width, this.canvas.height);
    }

    /** 设置视口变换 */
    setViewportTransform(viewportTransform: TMat2D) {
        const oldZoom = this.viewportTransform[0];
        const oldTranslateX = this.viewportTransform[4];
        const oldTranslateY = this.viewportTransform[5];

        const newZoom = viewportTransform[0];
        const newTranslateX = viewportTransform[4];
        const newTranslateY = viewportTransform[5];

        this.viewportTransform[0] = newZoom;
        this.viewportTransform[3] = newZoom;
        this.viewportTransform[4] = newTranslateX;
        this.viewportTransform[5] = newTranslateY;

        // 触发缩放事件
        if (oldZoom !== newZoom) {
            this.service.events.emit(__zoomEventName, newZoom);
        }
        // 触发平移事件
        if (oldTranslateX !== newTranslateX || oldTranslateY !== newTranslateY) {
            this.service.events.emit(__panEventName, newTranslateX, newTranslateY);
        }
    }

    /** 获取内容区域居中缩放内边距 */
    get fitPadding() {
        return this.__fitPadding;
    }

    /** 设置内容区域居中缩放内边距 */
    setFitPadding(padding: number | number[]) {
        if (isArray(padding)) {
            const vertical = padding[0] ?? this.__fitPadding[0];
            const horizontal = padding[1] ?? vertical;
            this.__fitPadding = [vertical, horizontal];
        } else {
            this.__fitPadding = [padding, padding];
        }
    }

    /** 将首次长画布布局定位到第一页顶部 */
    alignLongImageViewportToStart() {
        if (!this.isLongImageMode) return false;
        const { vertical } = this.__calcScrollInfo();
        if (!vertical.needScrollbar) return false;

        const viewportTransform: TMat2D = [...this.viewportTransform];
        viewportTransform[5] = this.__fitPadding[0];
        this.setViewportTransform(viewportTransform);
        return true;
    }

    /**
     * 获取缩放比例，不包含 retina 缩放
     * @returns - 缩放比例
     */
    get zoom() {
        return this.viewportTransform[0];
    }

    /** 获取位移 X，不包含 retina 缩放  */
    get translateX() {
        return this.viewportTransform[4];
    }

    /** 获取位移 Y，不包含 retina 缩放 */
    get translateY() {
        return this.viewportTransform[5];
    }

    /**
     * 获取渲染的缩放和位移，包含 retina 缩放
     * 用于渲染对象时使用
     * @returns - 缩放和位移
     */
    get renderZoomAndTranslate() {
        const retinaScaling = this.retinaScaling;
        const zoom = this.zoom * retinaScaling;
        const translateX = this.translateX * retinaScaling;
        const translateY = this.translateY * retinaScaling;
        return {
            zoom,
            translateX,
            translateY,
        };
    }

    /** 获取盲区 */
    get blindAreas(): TBlindAreas {
        return mergeBlindAreas(this.__defaultBlindAreas, this.__dynamicBlindAreas);
    }

    /** 无限画布模式 */
    get isEdgelessMode() {
        return this.renderMode === RenderMode.EDGELESS;
    }

    /** 单画布模式 */
    get isSingleMode() {
        return this.renderMode === RenderMode.SINGLE;
    }

    /** 长图模式 */
    get isLongImageMode() {
        return this.renderMode === RenderMode.VARIETY;
    }

    /** 设置缩放 */
    setZoom(zoom: number, eventPoint?: IPoint) {
        eventPoint = eventPoint ?? this.service.editor.getCenterPoint();
        this.zoomToPoint(eventPoint, zoom);
        this.calcViewportTransform();
    }

    /**
     * 设置缩放限制值
     * @param args - 缩放限制值
     * args.max - 最大缩放值
     * args.min - 最小缩放值
     * args.init - 初始缩放值，默认 1
     */
    zoomInit(args: { max?: number; min?: number; init?: number; translateX?: number; translateY?: number }) {
        const { max, min, init = 1, translateX = 0, translateY = 0 } = args;
        if (isNumber(min)) this.setMinZoom(min);
        if (isNumber(max)) this.setMaxZoom(max);
        const zoom = clamp(init, this.minZoom, this.maxZoom);
        this.setViewportTransform([zoom, 0, 0, zoom, translateX, translateY]);
        return this;
    }

    /** 缩小缩放 */
    zoomOut() {
        const zoom = this.zoom;
        const minZoom = this.minZoom;
        const newZoom = Math.max(minZoom, Math.floor(Math.max(minZoom, zoom * 0.9) * 10) / 10);
        if (newZoom !== zoom) {
            this.setIsAutoScale(false);
            this.setZoom(newZoom);
        }
    }

    /** 放大缩放 */
    zoomIn() {
        const zoom = this.zoom;
        const newZoom = Math.ceil(Math.min(this.maxZoom, zoom * 1.1) * 10) / 10;
        if (newZoom !== zoom) {
            this.setIsAutoScale(false);
            this.setZoom(newZoom);
        }
    }

    /**
     * 缩放视口到点
     * @param viewportPoint - 点
     * @param zoom - 缩放值
     * @returns
     */
    zoomToPoint(eventPoint: XY, zoom: number) {
        const doZoom = (viewportPoint: XY) => {
            const before = viewportPoint;
            const vpt: TMat2D = [...this.viewportTransform];
            const newPoint = transformPoint(viewportPoint, invertTransform(vpt));
            vpt[0] = zoom;
            vpt[3] = zoom;
            const after = transformPoint(newPoint, vpt);
            vpt[4] += before.x - after.x;
            vpt[5] += before.y - after.y;
            this.setViewportTransform(vpt);
            this.requestRenderAll();
        };

        if (this.isEdgelessMode) {
            doZoom(this.service.transform.getViewportPoint(eventPoint));
        } else {
            this.setIsAutoScale(false);

            const { transform } = this.service;
            const centerPoint = this.service.editor.getCenterPoint();

            if (this.zoom > zoom) {
                // 缩小：按滚动条状态计算平移，使视口相对内容位置合理
                const { horizontal, vertical, box } = this.__calcScrollInfo(zoom);
                const size = this.size;
                const centerTx = (size.width - box.width * zoom) / 2;
                const centerTy = (size.height - box.height * zoom) / 2;
                const calcFixedAxis = (axis: 'x' | 'y'): number => {
                    const point =
                        axis === 'x' ? { ...eventPoint, y: centerPoint.y } : { ...eventPoint, x: centerPoint.x };
                    const before = transform.getViewportPoint(point);
                    const vpt: TMat2D = [...this.viewportTransform];
                    const newPoint = transformPoint({ ...before }, invertTransform(vpt));
                    vpt[0] = zoom;
                    vpt[3] = zoom;
                    const after = transformPoint(newPoint, vpt);
                    return axis === 'x' ? vpt[4] + (before.x - after.x) : vpt[5] + (before.y - after.y);
                };
                const tx =
                    horizontal.offset.left && horizontal.offset.right
                        ? calcFixedAxis('x')
                        : !horizontal.offset.left && horizontal.offset.right
                          ? this.__fitPadding[1]
                          : horizontal.offset.left && !horizontal.offset.right
                            ? -(box.width * zoom - size.width) - this.__fitPadding[1]
                            : centerTx;
                const ty =
                    vertical.offset.top && vertical.offset.bottom
                        ? calcFixedAxis('y')
                        : !vertical.offset.top && vertical.offset.bottom
                          ? this.__fitPadding[0]
                          : vertical.offset.top && !vertical.offset.bottom
                            ? -(box.height * zoom - size.height) - this.__fitPadding[0]
                            : centerTy;
                this.setViewportTransform([zoom, 0, 0, zoom, tx, ty]);
                this.requestRenderAll();
            } else if (this.zoom < zoom) {
                // 放大：以中心或事件点（在 stage 内且该方向无滚动条时）为锚点
                const { frame } = this.service;
                const { horizontal, vertical } = this.__calcScrollInfo(zoom);
                const inStage = transform.checkPointInFrame(eventPoint, frame.getActiveIndex());
                const point = inStage
                    ? {
                          x: horizontal.needScrollbar ? eventPoint.x : centerPoint.x,
                          y: vertical.needScrollbar ? eventPoint.y : centerPoint.y,
                      }
                    : centerPoint;
                doZoom(transform.getViewportPoint(point));
            }
        }
    }

    /**
     * 平移视口
     * @param point - 平移距离
     * @returns
     */
    relativePan(delta: XY) {
        const doPan = (x: number, y: number) => {
            const vpt: TMat2D = [...this.viewportTransform];
            vpt[4] += x;
            vpt[5] += y;
            this.setViewportTransform(vpt);
            this.requestRenderAll();
        };
        if (this.isEdgelessMode) {
            doPan(delta.x, delta.y);
        } else {
            const { x, y } = this.__fixDeltaXY(this.__calcScrollInfo(), delta.x, delta.y);
            if (x !== 0 || y !== 0) {
                doPan(x, y);
            }
        }
    }

    /**
     * 过渡视口变换
     * @param vpt - 新的视口变换
     */
    transition(vpt: TMat2D, _isComplete: boolean, forceRerender: boolean) {
        this.setViewportTransform(vpt);
        // console.log('transition', forceRerender, this.autoFit);

        if (forceRerender) {
            this.requestRenderAll();
        }
    }

    override renderAll() {
        if (this.service.frame.isReplacingLayer) {
            this.requestRenderAll();
            return;
        }
        this._cancelRequestedRender();
        // if (this.__destroyed) return;
        // console.log('render');
        this._computeFPS();
        this._render();
    }

    /** 遍历渲染 */
    traverseRender(fn: (args: { object?: EdgelessObject; frame?: EdgelessFrame }) => void) {
        const renderStageList = this.isSingleMode
            ? [this.service.frame.getActiveFrameId()]
            : this.service.frame.getRenderFrameIds();

        // console.log('traverseRender ==>', renderStageList);

        const notFrameStageIdsMap: Map<number, string[]> = new Map();

        /** 最先按顺序渲染 frame */
        for (let i = 0; i < renderStageList.length; i++) {
            const originIndex = renderStageList[i];
            const frame = this.service.frame.get(originIndex);
            if (frame) {
                fn({ frame });
            } else {
                const renderIds = this.service.frame.getLayerIds(originIndex);
                notFrameStageIdsMap.set(originIndex, renderIds);
            }
        }

        /** 普通图层，渲染在 frame 之上 */
        notFrameStageIdsMap.forEach((renderIds, originIndex) => {
            const stickyOutIds = this.service.frame.getStickyOutFlatIds(originIndex);
            this.service.flatLayer.traverseIds(true, renderIds, (layerId: string) => {
                if (stickyOutIds.has(layerId)) return;
                const object = this.getByLayerId(layerId);
                if (object) {
                    fn({ object });
                }
            });
        });

        /** 粘性移入图层，渲染在最上层 */
        notFrameStageIdsMap.forEach((_, originIndex) => {
            this.service.frame.traverseStickyIn(originIndex, (layerId: string) => {
                const object = this.getByLayerId(layerId);
                if (object) {
                    fn({ object });
                }
            });
        });

        /** runtime-only 临时图层，渲染在最上层 */
        if (this.isEdgelessMode) {
            notFrameStageIdsMap.forEach((_, originIndex) => {
                this.service.frame.getRuntimeLayer(originIndex).forEach((layer) => {
                    const object = this.getByLayerId(layer.id);
                    if (object) {
                        fn({ object });
                    }
                });
            });
        }
    }

    protected override _render() {
        this.clearContext();

        if (this.hiddenAll) return;

        // console.time('renderCanvas ====>');
        this.traverseRender(({ object, frame }) => {
            try {
                if (object) {
                    if (object.shouldSkipRender()) return;
                    this.renderObject(this.ctx, object);
                } else if (frame) {
                    // console.log('renderFrame ==>', frame.frameId);
                    this.__renderFrame(this.ctx, frame);
                }
            } catch (error) {
                // 单个素材失败不能让后续素材整批消失，保留 ID 便于定位异常资源。
                console.error('Canvas render failed', { layerId: object?.layerId, frameId: frame?.frameId }, error);
            }
        });
        this.renderPlugins();

        // console.timeEnd('renderCanvas ====>');
    }

    /** 渲染插件 */
    renderPlugins() {
        for (const plugin of this.__pluginsSorted) {
            plugin.render();
        }
        // console.log('dirtyFrameIds', Array.from(this.dirtyFrameIds).join(','));
        // 清空脏图层 id 列表
        this.dirtyFrameIds.clear();
    }

    /**
     * 渲染对象
     * @param ctx - 画布上下文
     * @param object - 对象
     */
    renderObject(ctx: CanvasRenderingContext2D, object: EdgelessObject) {
        if (object.visible && !object.renderHidden && object.checkInViewport().intersects) {
            if (object.domRender) {
                // console.log('domRender', object);
                this.__emitDomRender(object, true, false);
                return;
            }
            const { zoom, translateX, translateY } = this.renderZoomAndTranslate;
            ctx.save();
            try {
                ctx.translate(object.left * zoom + translateX, object.top * zoom + translateY);
                ctx.rotate((Math.PI / 180) * object.angle);
                ctx.scale(zoom, zoom);
                if (object.opacity !== 1) {
                    ctx.globalAlpha = object.opacity;
                }
                object.render(ctx);
            } finally {
                ctx.restore();
            }
        } else {
            this.__emitDomRender(object, false, true);
        }
    }

    /** 监听 dom 渲染事件 */
    onDomRender(fn: (args: { object: EdgelessObject; show: boolean; hide: boolean }) => void, widget?: number) {
        return this.service.events.on(__domRenderEventName, fn, widget);
    }

    /** 触发 dom 渲染事件 */
    private __emitDomRender(object: EdgelessObject, show: boolean, hide: boolean) {
        this.service.events.emit(__domRenderEventName, { object, show, hide });
    }

    /** 渲染 frame */
    private __renderFrame(ctx: CanvasRenderingContext2D, frame: EdgelessFrame) {
        if (this.isEdgelessMode) {
            frame.renderInEdgeless(ctx);
        } else if (this.isSingleMode) {
            this.requestFrameResources(frame.frameId);
            frame.renderInSingle(ctx);
        } else if (this.isLongImageMode) {
            if (frame.checkInViewport().intersects) {
                this.requestFrameResources(frame.frameId);
            } else if (this.hasDeferredFrameResources(frame.frameId)) {
                return;
            }
            frame.renderInVariety(ctx);
        }
    }

    /** 获取画布上下文 */
    getContext(): CanvasRenderingContext2D {
        return this.ctx;
    }

    /** 清空画布上下文 */
    clearContext() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /** 填充画布上下文 */
    fillContext(color: string) {
        this.ctx.save();
        this.ctx.fillStyle = color;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.restore();
    }

    /** 恢复盲区 */
    restoreBlindAreas() {
        const previousBlindAreas = this.blindAreas;
        this.__dynamicBlindAreas = null;
        this.__emitBlindAreasChange(previousBlindAreas);
        return this;
    }

    /**
     * 设置盲区
     * 当有业务模块覆盖在无限画布上时，需要设置盲区，以避免业务模块遮挡无限画布
     * @param blindAreas - 无限画布盲区
     */
    setBlindAreas(blindAreas: Partial<TBlindAreas>, options = { dynamic: false }) {
        const { dynamic } = options;
        const previousBlindAreas = this.blindAreas;

        if (dynamic) {
            this.__dynamicBlindAreas = {
                ...this.__dynamicBlindAreas,
                ...blindAreas,
            };
            this.__emitBlindAreasChange(previousBlindAreas);
            return this;
        }

        this.__defaultBlindAreas = {
            ...this.__defaultBlindAreas,
            ...blindAreas,
        };

        this.__emitBlindAreasChange(previousBlindAreas);

        return this;
    }

    /** 有效盲区变化时通知订阅方 */
    private __emitBlindAreasChange(previousBlindAreas: TBlindAreas) {
        const currentBlindAreas = this.blindAreas;
        if (isSameBlindAreas(previousBlindAreas, currentBlindAreas)) return;

        this.service.events.emit(__blindAreasChangeEventName, { ...currentBlindAreas });
    }

    /** 计算画布内容边界框大小 */
    calcContentBox() {
        const points: XY[] = [];
        this.traverseRender(({ object, frame }) => {
            if (object) points.push(...object.getCoords());
            else if (frame) points.push(...frame.getCoords());
        });
        const boundingBox = makeBoundingBoxFromPoints(points);
        boundingBox.width = Math.ceil(boundingBox.width);
        boundingBox.height = Math.ceil(boundingBox.height);
        boundingBox.left = Math.floor(boundingBox.left);
        boundingBox.top = Math.floor(boundingBox.top);
        return boundingBox;
    }

    /** 添加脏 frameId */
    addDirtyFrameId(frameId: number) {
        this.dirtyFrameIds.add(frameId);
        const frame = this.service.frame.get(frameId);
        if (frame) {
            frame.renderPreview();
        }
    }

    /** 设置对象脏状态，重新渲染对象 */
    setDirty(args: { object: EdgelessObject; isAdd?: boolean; onlyUpdateBaseInfo?: boolean }) {
        const { object, isAdd, onlyUpdateBaseInfo } = args;

        const layer =
            this.service.flatLayer.getLayer(object.layerId) || this.service.frame.getRuntimeLayer(object.layerId);
        const isBaseOperate = this.service.editor.getIsBaseOperate();
        // 设置需要重新渲染的脏 frameId 列表
        if (layer && (isBaseOperate ? !layer?.parentId : true)) {
            if (isNumber(layer.__stickyFrameId) && isNumber(layer.__preStickyFrameId)) {
                this.addDirtyFrameId(layer.__stickyFrameId);
                this.addDirtyFrameId(layer.__preStickyFrameId);
            } else {
                if (isNumber(layer?.__stickyFrameId)) {
                    this.addDirtyFrameId(layer.__stickyFrameId);
                }
                if (isNumber(layer?.__preStickyFrameId)) {
                    this.addDirtyFrameId(layer.__preStickyFrameId);
                }
                if (isNumber(layer?.__originIndex)) {
                    this.addDirtyFrameId(layer.__originIndex);
                }
            }
            // console.log(
            //     'setDirty=>',
            //     layer.layerType,
            //     layer.__stickyFrameId,
            //     layer.__preStickyFrameId,
            //     layer.__originIndex,
            // );
        } else {
            if (isNumber(layer?.__originIndex)) {
                this.addDirtyFrameId(layer.__originIndex);
            }
        }

        // console.log('setDirty');

        // 组对象不需要重新渲染
        if (object.noRender) return;

        if (!isAdd) {
            // console.log('setDirty=>', object.layerId, isAdd);
            if (onlyUpdateBaseInfo) {
                // 移动、旋转、缩放、尺寸调整，在基础操作里调用渲染
            } else {
                this.requestRenderAll();
            }
        } else {
            if (this.__loadedTimer) clearTimeout(this.__loadedTimer);
            this.__loadedTimer = setTimeout(() => {
                this.requestRenderAll();
            }, 10);
        }
    }

    /**
     * 添加对象
     * @param objects - 对象
     * @returns - 添加的对象数量
     */
    add(...objects: EdgelessObject[]) {
        objects.forEach((object) => {
            const layerId = object.layerId;
            this.__objectsMap[layerId] = object;
        });
    }

    /**
     * 移除对象
     * @param objects - 对象
     * @returns - 移除的对象
     */
    remove(...objects: EdgelessObject[]) {
        objects.forEach((object) => {
            this.__cancelObjectResource(object);
            delete this.__objectsMap[object.layerId];
            if (this.plugins.focus.exit(object)) {
                this.restoreBlindAreas();
            }
            object.dispose();
        });
    }

    /** 根据当前图层数据重建所有渲染对象 */
    rebuildObjectsFromFlatLayer() {
        const oldObjects = Object.values(this.__objectsMap);
        oldObjects.forEach((object) => {
            this.remove(object);
            if (object.domRender) {
                this.__emitDomRender(object, false, true);
            }
        });

        Object.values(this.service.flatLayer.data).forEach((layer) => {
            const object = EdgelessObject.create(layer, this.service);
            if (!object) return;

            this.__initLayerObject(object);
        });

        this.service.frame.getRenderFrameIds().forEach((frameId) => {
            this.addDirtyFrameId(frameId);
        });
        this.refreshScrollInfo();
        this.requestRenderAll();
    }

    /** 获取对象数量 */
    get length() {
        const renderStageList = this.service.frame.getRenderFrameIds();
        let count = 0;
        for (const originIndex of renderStageList) {
            count += this.service.frame.getLayerIds(originIndex).length;
            // count += this.service.frame.getRuntimeLayer(originIndex).length;
        }
        return count;
    }

    /** 根据图层id获取边界框 */
    getBoundingBox(layerId: string): EdgelessBoundingBox | null {
        const layer = this.service.flatLayer.getLayer(layerId) || this.service.frame.getRuntimeLayer(layerId);
        if (!layer) return null;
        return new EdgelessBoundingBox(layer.left, layer.top, layer.width, layer.height, layer.rotate);
    }

    /**
     * 显示所有对象
     * @param fitFromCenter - 是否从中心开始缩放
     * @param fitAnimate - 是否居中缩放动画
     */
    showAllObjects(fitFromCenter = false, fitAnimate = true) {
        this.__fitViewportId += 1;
        const allBox: EdgelessBoundingBox[] = [];
        this.traverseRender(({ object, frame }) => {
            if (object) {
                // 重新计算边界框，剔除描边等特效信息
                const box = this.getBoundingBox(object.layerId);
                if (box) allBox.push(box);
            } else if (frame) {
                allBox.push(frame);
            }
        });
        return this.__makeObjectsFitViewport(this.__fitViewportId, allBox, fitFromCenter, fitAnimate);
    }

    /**
     * 将对象居中
     * @param objects - 对象
     * @param fitFromCenter - 是否从中心开始缩放
     * @param fitAnimate - 是否居中缩放动画
     * @param forceRerender - 是否强制重新渲染
     */
    setObjectToCenter(
        objects: EdgelessBoundingBox | EdgelessBoundingBox[],
        fitFromCenter?: boolean,
        fitAnimate?: boolean,
        forceRerender?: boolean,
    ) {
        this.__fitViewportId += 1;
        return this.__makeObjectsFitViewport(
            this.__fitViewportId,
            isArray(objects) ? objects : [objects],
            fitFromCenter,
            fitAnimate,
            forceRerender,
        );
    }

    /**
     * 将 frame 居中
     * @param frameId - frameId
     * @param fitFromCenter - 是否从中心开始缩放
     * @param fitAnimate - 是否居中缩放动画
     * @param forceRerender - 是否强制重新渲染
     */
    setFrameToCenter(frameId: number, fitFromCenter?: boolean, fitAnimate?: boolean, forceRerender?: boolean) {
        const box = this.service.frame.get(frameId);
        if (box) {
            this.setObjectToCenter(box, fitFromCenter, fitAnimate, forceRerender);
        }
    }

    /**
     * 将对象居中
     * @param layerId - 图层 ID 或 图层 ID 数组
     * @returns
     */
    setObjectToCenterByLayerId(layerId: string | string[]) {
        if (isArray(layerId)) {
            const boxes = layerId.map((id) => this.getBoundingBox(id)).filter((item) => item !== null);
            if (boxes.length > 0) {
                return this.setObjectToCenter(boxes as EdgelessBoundingBox[]);
            }
        } else {
            const box = this.getBoundingBox(layerId);
            if (box) {
                return this.setObjectToCenter(box);
            }
        }
        return this;
    }

    private __getUnobstructedArea() {
        const containerSize = this.getSize();
        const blindAreas = this.blindAreas;

        const width = containerSize.width - blindAreas.left - blindAreas.right;
        const height = containerSize.height - blindAreas.top - blindAreas.bottom;

        return {
            width,
            height,
        };
    }

    private __calcObjectsBound(objects: EdgelessBoundingBox[]) {
        let left = Infinity;
        let top = Infinity;
        let right = -Infinity;
        let bottom = -Infinity;

        for (let i = 0; i < objects.length; i++) {
            const objectBound = objects[i].getBoundingRect();
            left = Math.min(left, objectBound.left);
            top = Math.min(top, objectBound.top);
            right = Math.max(right, objectBound.left + objectBound.width);
            bottom = Math.max(bottom, objectBound.top + objectBound.height);
        }

        const width = right - left;
        const height = bottom - top;

        return {
            left,
            top,
            right,
            bottom,
            width,
            height,
        };
    }

    /**
     * 将对象居中
     * @param fitViewportId - 居中缩放计数器
     * @param objects - 对象
     * @param fitFromCenter - 是否居中
     * @param animate - 是否动画
     * @param forceRerender - 是否强制重新渲染
     */
    private __makeObjectsFitViewport(
        fitViewportId: number,
        objects: EdgelessBoundingBox[],
        fitFromCenter = false,
        fitAnimate = true,
        forceRerender = true,
    ) {
        if (objects.length === 0) return this;

        const duration = this.__fitDuration;
        const padding = this.__fitPadding;
        const blindAreas = this.blindAreas;

        const { width, height, left, top } = this.__calcObjectsBound(objects);
        const center = {
            x: left + width / 2,
            y: top + height / 2,
        };

        const unobstructedArea = this.__getUnobstructedArea();
        const scaleX = (unobstructedArea.width - padding[1] * 2) / width;
        const scaleY = (unobstructedArea.height - padding[0] * 2) / height;
        const minZoom = this.minZoom;
        const maxZoom = this.maxZoom;

        const targetZoom = clamp(Math.min(scaleX, scaleY), minZoom, maxZoom);
        const targetTranslation = {
            x: blindAreas.left + unobstructedArea.width / 2 - center.x * targetZoom,
            y: blindAreas.top + unobstructedArea.height / 2 - center.y * targetZoom,
        };

        if (fitFromCenter) {
            const initTranslation = {
                x: blindAreas.left + unobstructedArea.width / 2 - center.x * this.zoom,
                y: blindAreas.top + unobstructedArea.height / 2 - center.y * this.zoom,
            };
            const transform: TMat2D = [...this.viewportTransform];
            transform[4] = initTranslation.x;
            transform[5] = initTranslation.y;
            this.setViewportTransform(transform);
        }

        if (!fitAnimate) {
            const vpt: TMat2D = [targetZoom, 0, 0, targetZoom, targetTranslation.x, targetTranslation.y];
            const isComplete = true;
            this.transition(vpt, isComplete, forceRerender);
            return this;
        }

        // console.log('setObjectToCenter animate', forceRerender);

        const currentZoom = this.zoom;
        const currentTranslation = {
            x: this.translateX,
            y: this.translateY,
        };

        const startTime = Date.now();

        const transition = () => {
            if (fitViewportId !== this.__fitViewportId) return;
            const now = Date.now();
            const progress = Math.min(1, (now - startTime) / duration);
            const eased = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            const translation = {
                x: currentTranslation.x + (targetTranslation.x - currentTranslation.x) * eased,
                y: currentTranslation.y + (targetTranslation.y - currentTranslation.y) * eased,
            };
            const zoom = currentZoom + (targetZoom - currentZoom) * eased;
            const vpt: TMat2D = [zoom, 0, 0, zoom, translation.x, translation.y];

            const isComplete = progress < 1 ? false : true;
            this.transition(vpt, isComplete, forceRerender);

            if (!isComplete) requestAnimationFrame(transition);
        };

        transition();

        return this;
    }
}
